import { Map, MapBrowserEvent, View } from 'ol';
import apply from 'ol-mapbox-style';
import { FeatureLike } from 'ol/Feature';
import GeoJSON, { GeoJSONFeature, GeoJSONFeatureCollection } from 'ol/format/GeoJSON';
import LayerGroup from 'ol/layer/Group';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Fill, Icon, Stroke, Style } from 'ol/style';
import { RefObject } from 'react';
import markerGreen from '../../assets/marker-green.png';
import markerRed from '../../assets/marker-red.png';
import markerYellow from '../../assets/marker-yellow.png';
import { DEVICE_LAYER_CLASS, DEVICESETS_LAYER_CLASS, ELEMENTS_LAYER_CLASSES, MAP_PROJECTION_OL, MAP_TYPES, MAPBOX_ROADMAP_URL, MAPBOX_SATELLITE_URL, SEVERITY_COLORS, WKTOPTIONS } from '../../constants/map.constants';

const SEVERITY_MARKERS = { '0': markerGreen, '100': markerYellow, '200': markerRed };

export async function initMap(mapElement: RefObject<HTMLDivElement>): Promise<Map> {
    const map = new Map({
        target: mapElement.current ?? undefined,
        layers: [],
        view: new View({
            projection: MAP_PROJECTION_OL,
            zoom: 5,
        }),
        controls: [],
        overlays: [],
    });

    await apply(map, MAPBOX_ROADMAP_URL, {});

    const groupRoadmap = new LayerGroup({
        visible: true,
        properties: { mapType: MAP_TYPES.ROADMAP },
    });
    map.addLayer(groupRoadmap);
    map.getLayers()
        .getArray()
        .slice()
        .forEach(function (layer) {
            if (!(layer instanceof LayerGroup)) {
                map.removeLayer(layer);
                groupRoadmap.getLayers().push(layer);
            }
        });

    await apply(map, MAPBOX_SATELLITE_URL);

    const groupSatellite = new LayerGroup({
        visible: false,
        properties: { mapType: MAP_TYPES.SATELLITE },
    });
    map.addLayer(groupSatellite);
    map.getLayers()
        .getArray()
        .slice()
        .forEach(function (layer) {
            if (!(layer instanceof LayerGroup)) {
                map.removeLayer(layer);
                groupSatellite.getLayers().push(layer);
            }
        });
    return map;
}

export function getPointStyle(feature: FeatureLike) {
    if (feature.getProperties().hide) {
        return new Style({});
    }
    const severity = feature.getProperties()['details']['status_severity'];
    const marker = SEVERITY_MARKERS[severity as '0' | '100' | '200'];
    const stylePoint =
        new Style({
            image: new Icon({
                src: marker,
                width: 40,
                anchorXUnits: 'fraction',
                anchorYUnits: 'fraction',
                anchor: [0.5, 1],
            }),
        });
    return stylePoint;
}

export function getPolygonStyle(feature: FeatureLike) {
    const severity = feature.getProperties()['details']['status_severity'];
    const color = SEVERITY_COLORS.hasOwnProperty(severity) ? SEVERITY_COLORS[severity as '0' | '100' | '200'] : '#000000';
    const colorLighter = color.substring(0, 7) + '33';

    const zoneFill = new Fill({
        color: colorLighter,
    });
    const zoneStroke = new Stroke({
        width: 1,
        lineDash: undefined,
        color: color,
    });
    const zoneStyle = new Style({
        fill: zoneFill,
        stroke: zoneStroke,
    });
    return zoneStyle;
}

export function getLineStringStyle(feature: FeatureLike) {
    const severity = feature.getProperties()['details']['status_severity'];
    const color = SEVERITY_COLORS.hasOwnProperty(severity) ? SEVERITY_COLORS[severity as '0' | '100' | '200'] : '#000000';
    return new Style({
        stroke: new Stroke({
            color: color,
            width: 3,
        }),
    });
}

export function styleFunction(feature: any) {
    const styles: Style[] = [getPointStyle(feature), getLineStringStyle(feature), getPolygonStyle(feature)];
    return styles;
}

export function buildDevicesetsLayer(devicesets: GeoJSONFeatureCollection) {
    const devicesetFeatures = new GeoJSON().readFeatures(
        devicesets,
        WKTOPTIONS
    );
    const source = new VectorSource({
        features: devicesetFeatures as any,
    });
    const layer = new VectorLayer<VectorSource>({
        visible: true,
        source: source,
        className: DEVICESETS_LAYER_CLASS,
        style: styleFunction,
        zIndex: 4000,
    });
    return layer;
}

export function buildDeviceLayer(device: GeoJSONFeature) {
    const devicesetFeatures = new GeoJSON().readFeatures(
        device,
        WKTOPTIONS
    );
    const source = new VectorSource({
        features: devicesetFeatures as any,
    });
    const layer = new VectorLayer<VectorSource>({
        visible: true,
        source: source,
        className: DEVICE_LAYER_CLASS,
        style: styleFunction,
        zIndex: 4000,
    });
    return layer;
}

export function getDevicesetTargeted(map: Map, event: MapBrowserEvent<UIEvent>) {
    return map.forEachFeatureAtPixel(
        event.pixel,
        function (feature, layer) {
            if (ELEMENTS_LAYER_CLASSES.includes(layer.getClassName())) {
                return feature;
            }
        }
    );
}
